/**
 * Investment ledgers: each mutual fund, FD or RD the company holds, under 1530.
 *
 * One ledger per holding, coded 1530-001, 1530-002 ... A sub-ledger of the
 * control account 1530 Current investments, so the strip at the top checks
 * that the ledgers add up to it. The Book Keeper opens a ledger and records on
 * it alone: no Finance Head approval (decided 26 Oct 2026).
 *
 * Money that moved -- a SIP instalment, an FD deposit, redemption proceeds --
 * reaches a ledger from its bank line (Bank statements, category Investment).
 * What moved no money is recorded here and posted at once: the opening
 * balance (given when the ledger is added, or later on its own card), interest
 * or returns earned (with TDS), a loss on redemption. A correction reverses the
 * entry and posts the corrected one; both stay.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Field, Loading, Note, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, matches, rowProps } from '../components/SidePanel'

interface Investment {
  id: string
  code: string
  name: string
  label: string
  status: string
  investment_type: string
  type_label: string
  institution: string | null
  reference: string | null
  started_on: string | null
  maturity_on: string | null
  days_to_maturity: number | null
  rate_bp: number | null
  maturity_amount_paise: number | null
  notes: string | null
  opening_paise: number
  invested_paise: number
  total_invested_paise: number
  redeemed_paise: number
  returns_paise: number
  tds_paise: number
  balance_paise: number
  raised_by: string | null
}

interface Book {
  account_code: string | null
  account_name: string | null
  as_at: string
  investments: Investment[]
  control_paise: number
  ledgers_paise: number
  unassigned_paise: number
  difference_paise: number
  maturing_soon: number
  //: The day these books begin: the earliest an opening balance may be dated.
  books_begin: string | null
}

interface HistoryLine {
  date: string
  journal_entry_id: string
  journal_number: string
  narration: string
  //: opening / invested / redeemed / return / loss
  kind: string
  reversal: boolean
  holding_paise: number
  returns_paise: number
  tds_paise: number
  balance_paise: number
  bank_transaction_id: string | null
  bank_reference: string | null
  entry_id: string | null
}

interface Entry {
  id: string
  kind: EntryKind
  kind_label: string
  entry_date: string
  description: string
  notes: string | null
  amount_paise: number
  tds_paise: number
  status: 'posted' | 'reversed'
  journal_number: string | null
  reversal_number: string | null
  replaced_by_id: string | null
  reason: string | null
  recorded_by: string | null
  version: number
}

interface History {
  investment: Investment
  lines: HistoryLine[]
  entries: Entry[]
  //: The window an opening balance may be dated in.
  opening_earliest: string | null
  opening_latest: string
}

type Tab = 'active' | 'closed' | 'all'
type EntryKind = 'opening' | 'return' | 'loss'

const TYPES: { value: string; label: string }[] = [
  { value: 'mutual_fund', label: 'Mutual fund' },
  { value: 'fixed_deposit', label: 'Fixed deposit' },
  { value: 'recurring_deposit', label: 'Recurring deposit' },
  { value: 'other', label: 'Other' },
]

const ENTRY_KINDS: { value: EntryKind; label: string; caption: string }[] = [
  {
    value: 'opening',
    label: 'Opening balance',
    caption: 'what it stood at when these books began',
  },
  {
    value: 'return',
    label: 'Interest or returns earned',
    caption: 'kept in the investment: FD/RD interest credited to it, a gain on redemption',
  },
  {
    value: 'loss',
    label: 'Loss on redemption',
    caption: 'less came back than was put in',
  },
]

const LINE_LABEL: Record<string, string> = {
  opening: 'opening balance',
  invested: 'invested',
  redeemed: 'redeemed',
  return: 'returns',
  loss: 'loss',
}

const rupees = (paise: number | null) => (paise ? String(paise / 100) : '')
const toPaise = (value: string) => Math.round(Number(value) * 100)
//: The local date: the server refuses a date after today in India.
const todayHere = () => new Date().toLocaleDateString('en-CA')

//: Why an opening balance cannot carry this date, or null.
function openingDateProblem(day: string, earliest: string | null, latest: string) {
  if (!day) return 'Give the date the balance is as at.'
  if (earliest && day < earliest) return `These books begin on ${earliest}; date it then or later.`
  if (day > latest)
    return latest < todayHere()
      ? `It must come before the ledger's first entry: date it ${latest} or earlier.`
      : 'The date cannot be in the future.'
  return null
}

function maturityNote(i: Investment) {
  if (i.days_to_maturity === null) return null
  if (i.days_to_maturity < 0) return `matured ${-i.days_to_maturity} days ago`
  if (i.days_to_maturity === 0) return 'matures today'
  return `in ${i.days_to_maturity} days`
}

export default function InvestmentLedgers() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('active')
  const [search, setSearch] = useState('')
  const [asAt, setAsAt] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const book = useQuery({
    queryKey: ['investment-ledgers', asAt],
    queryFn: () =>
      api.get<Book>(`/investments?${new URLSearchParams(asAt ? { as_at: asAt } : {}).toString()}`),
  })
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['investment-ledgers'] })
    void queryClient.invalidateQueries({ queryKey: ['investment-history'] })
  }

  const rows = book.data?.investments ?? []
  const active = rows.filter((r) => r.status === 'approved')
  const shown = rows.filter(
    (r) =>
      (tab === 'all' || (tab === 'active' ? r.status === 'approved' : r.status !== 'approved')) &&
      matches(search, r.code, r.name, r.institution, r.reference, r.type_label),
  )
  const paged = usePaged(shown, `${tab}|${search}`)
  const opened = rows.find((r) => r.id === open) ?? null

  if (book.isLoading) return <Loading what="Loading investment ledgers" />
  const b = book.data
  const off = b ? b.difference_paise + b.unassigned_paise : 0

  return (
    <>
      <ErrorNote error={book.error} />

      {can('party.vendor.create') && (
        <div className="bank-toolbar">
          <button className="primary" onClick={() => setAdding(true)}>
            Add an investment
          </button>
        </div>
      )}

      {b && (
        <div className="grid">
          <Stat
            label={`${b.account_code ?? '1530'} ${b.account_name ?? 'Current investments'}`}
            value={formatInr(b.control_paise)}
            note={b.account_code ? `held on ${b.as_at}` : 'the account is not in the chart yet'}
          />
          <Stat
            label="Investment ledgers"
            value={formatInr(b.ledgers_paise)}
            note={`${active.length} active ledgers`}
          />
          <Stat
            label="Difference"
            value={formatInr(off)}
            note={
              off === 0
                ? 'the ledgers add up to 1530'
                : 'part of 1530 names no ledger — look into it'
            }
          />
          <Stat
            label="Maturing soon"
            value={String(b.maturing_soon)}
            note="active ledgers maturing in the next 30 days"
          />
        </div>
      )}

      {b && !b.account_code && (
        <Note kind="warn">
          1530 Current investments is not in the chart of accounts yet. An Admin adds it from Chart
          of accounts → Add the missing standard accounts; until then nothing can be recorded here.
        </Note>
      )}
      {b && b.unassigned_paise !== 0 && (
        <Note kind="warn">
          {formatInr(b.unassigned_paise)} on {b.account_code} names no investment ledger. Every line
          on this account should name one; this is shown, never corrected automatically.
        </Note>
      )}

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              { key: 'active', label: 'Active', count: active.length },
              {
                key: 'closed',
                label: 'Matured or closed',
                count: rows.length - active.length,
                quietWhenZero: true,
              },
              { key: 'all', label: 'All', count: rows.length },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Code, name, bank or folio"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search investment ledgers"
            />
            <label className="appr-check">
              As at
              <input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)} />
            </label>
          </div>
        </header>
        <div className="body tight">
          {shown.length === 0 ? (
            <div className="empty">
              {rows.length === 0
                ? 'No investment ledgers yet. Add one for each mutual fund, FD or RD the company holds.'
                : 'No investment ledger matches that.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Investment</th>
                  <th>Type</th>
                  <th>Matures</th>
                  <th className="num">Total invested</th>
                  <th className="num">Returns</th>
                  <th className="num">Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((r) => (
                  <tr key={r.id} {...rowProps(() => setOpen(r.id), open === r.id)}>
                    <td>
                      <div className="strong">{r.name}</div>
                      <div className="cell-sub ref">
                        {r.code}
                        {r.institution ? ` · ${r.institution}` : ''}
                      </div>
                    </td>
                    <td>{r.type_label}</td>
                    <td>
                      {r.maturity_on ? (
                        <>
                          <div className="date">{r.maturity_on}</div>
                          <div className="cell-sub">{maturityNote(r)}</div>
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num">{formatInr(r.total_invested_paise)}</td>
                    <td className="num">{formatInr(r.returns_paise)}</td>
                    <td className="num strong">{formatInr(r.balance_paise)}</td>
                    <td>
                      <span className={`pill ${r.status === 'approved' ? 'approved' : 'draft'}`}>
                        {r.status === 'approved' ? 'active' : 'closed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      </section>

      {adding && (
        <DetailsPanel
          booksBegin={b?.books_begin ?? null}
          onClose={() => setAdding(false)}
          onDone={(id) => {
            refresh()
            setAdding(false)
            setOpen(id)
          }}
        />
      )}
      {opened && (
        <InvestmentPanel investment={opened} onClose={() => setOpen(null)} onChanged={refresh} />
      )}
    </>
  )
}

interface DetailsForm {
  name: string
  investment_type: string
  institution: string
  reference: string
  started_on: string
  maturity_on: string
  rate: string
  maturity_amount: string
  notes: string
}

function formOf(i?: Investment): DetailsForm {
  return {
    name: i?.name ?? '',
    investment_type: i?.investment_type ?? 'mutual_fund',
    institution: i?.institution ?? '',
    reference: i?.reference ?? '',
    started_on: i?.started_on ?? '',
    maturity_on: i?.maturity_on ?? '',
    rate: i?.rate_bp != null ? String(i.rate_bp / 100) : '',
    maturity_amount: rupees(i?.maturity_amount_paise ?? null),
    notes: i?.notes ?? '',
  }
}

function problemsOf(f: DetailsForm): string | null {
  if (!f.name.trim()) return 'Give the investment a name.'
  if (f.started_on && f.maturity_on && f.maturity_on < f.started_on)
    return 'It cannot mature before it starts.'
  if (f.rate && !(Number(f.rate) >= 0 && Number(f.rate) <= 100))
    return 'The rate is a yearly percentage between 0 and 100.'
  if (f.maturity_amount && !(Number(f.maturity_amount) > 0))
    return 'The maturity amount must be more than zero.'
  return null
}

function bodyOf(f: DetailsForm) {
  return {
    name: f.name.trim(),
    investment_type: f.investment_type,
    institution: f.institution.trim() || null,
    reference: f.reference.trim() || null,
    started_on: f.started_on || null,
    maturity_on: f.maturity_on || null,
    rate_bp: f.rate ? Math.round(Number(f.rate) * 100) : null,
    maturity_amount_paise: f.maturity_amount ? toPaise(f.maturity_amount) : null,
    notes: f.notes.trim() || null,
  }
}

function DetailsFields({
  form,
  setForm,
}: {
  form: DetailsForm
  setForm: (f: DetailsForm) => void
}) {
  const deposit =
    form.investment_type === 'fixed_deposit' || form.investment_type === 'recurring_deposit'
  return (
    <>
      <div className="row">
        <Field label="Type">
          <select
            value={form.investment_type}
            onChange={(e) => setForm({ ...form, investment_type: e.target.value })}
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Name" caption="as you know it, e.g. SBI MF SIP or HDFC FD 1234">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            maxLength={200}
          />
        </Field>
      </div>
      <div className="row">
        <Field label={deposit ? 'Bank' : 'Fund house or institution'} caption="optional">
          <input
            value={form.institution}
            onChange={(e) => setForm({ ...form, institution: e.target.value })}
            maxLength={200}
          />
        </Field>
        <Field label={deposit ? 'Deposit or account number' : 'Folio number'} caption="optional">
          <input
            className="mono"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            maxLength={80}
          />
        </Field>
      </div>
      <div className="row">
        <Field label="Started on" caption="optional">
          <input
            type="date"
            value={form.started_on}
            onChange={(e) => setForm({ ...form, started_on: e.target.value })}
          />
        </Field>
        <Field label="Maturity date" caption={deposit ? 'when the deposit matures' : 'optional'}>
          <input
            type="date"
            min={form.started_on || undefined}
            value={form.maturity_on}
            onChange={(e) => setForm({ ...form, maturity_on: e.target.value })}
          />
        </Field>
      </div>
      <div className="row">
        <Field label="Interest rate (% a year)" caption="optional, e.g. 7.25">
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.rate}
            onChange={(e) => setForm({ ...form, rate: e.target.value })}
          />
        </Field>
        <Field label="Maturity amount (₹)" caption="optional, as on the deposit receipt">
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={form.maturity_amount}
            onChange={(e) => setForm({ ...form, maturity_amount: e.target.value })}
          />
        </Field>
      </div>
      <Field label="Notes" caption="optional">
        <textarea
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          maxLength={2000}
        />
      </Field>
    </>
  )
}

function DetailsPanel({
  booksBegin,
  onClose,
  onDone,
}: {
  booksBegin: string | null
  onClose: () => void
  onDone: (id: string) => void
}) {
  const [form, setForm] = useState<DetailsForm>(formOf())
  //: Held before these books began: its opening balance, posted with the ledger.
  const [opening, setOpening] = useState({
    on: false,
    amount: '',
    as_at: booksBegin ?? '',
    basis: '',
  })
  const openingProblem = !opening.on
    ? null
    : !(toPaise(opening.amount) > 0)
      ? 'The opening balance must be more than zero.'
      : (openingDateProblem(opening.as_at, booksBegin, todayHere()) ??
        (opening.basis.trim().length < 3 ? 'Say where the opening figure comes from.' : null))
  const problem = problemsOf(form) ?? openingProblem
  const create = useMutation({
    mutationFn: () =>
      api.post<Investment>('/investments', {
        ...bodyOf(form),
        opening: opening.on
          ? {
              amount_paise: toPaise(opening.amount),
              as_at: opening.as_at,
              basis: opening.basis.trim(),
            }
          : null,
      }),
    onSuccess: (i) => onDone(i.id),
  })
  return (
    <SidePanel
      kicker="Investment ledger"
      title="Add an investment"
      sub="a ledger of its own under 1530 Current investments"
      onClose={onClose}
    >
      <ErrorNote error={create.error} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!problem) create.mutate()
        }}
      >
        <DetailsFields form={form} setForm={setForm} />
        <div className="card" style={{ margin: '8px 0' }}>
          <h3 style={{ margin: '0 0 8px' }}>Opening balance</h3>
          <label className="appr-check">
            <input
              type="checkbox"
              checked={opening.on}
              onChange={(e) => setOpening({ ...opening, on: e.target.checked })}
            />
            It was already held when these books began
            {booksBegin ? ` (${booksBegin})` : ''} — record what it stood at
          </label>
          {opening.on && (
            <>
              <div className="row">
                <Field label="Opening balance (₹)" caption="what the investment stood at">
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={opening.amount}
                    onChange={(e) => setOpening({ ...opening, amount: e.target.value })}
                    required
                  />
                </Field>
                <Field label="As at" caption="usually the day these books begin">
                  <input
                    type="date"
                    min={booksBegin ?? undefined}
                    max={todayHere()}
                    value={opening.as_at}
                    onChange={(e) => setOpening({ ...opening, as_at: e.target.value })}
                    required
                  />
                </Field>
              </div>
              <Field
                label="Where the figure comes from"
                caption="e.g. FD advice 31 Mar 2026, CAS statement, CA's confirmation"
              >
                <input
                  value={opening.basis}
                  onChange={(e) => setOpening({ ...opening, basis: e.target.value })}
                  maxLength={300}
                  required
                />
              </Field>
            </>
          )}
          {!opening.on && (
            <p className="muted" style={{ fontSize: 14, margin: '4px 0 0' }}>
              Leave it unticked for an investment started after the books began: its money comes in
              from the bank line.
            </p>
          )}
        </div>
        {problem && form.name && <Note kind="warn">{problem}</Note>}
        <p className="muted" style={{ fontSize: 14 }}>
          The ledger gets the next code (1530-001, 1530-002 …) and can be chosen on a bank line at
          once. Add one ledger for each holding: each SIP, each FD, each RD. An opening balance is
          posted to the books with it.
        </p>
        <div className="actions">
          <button className="primary" type="submit" disabled={create.isPending || !!problem}>
            {create.isPending ? 'Adding…' : 'Add investment'}
          </button>
        </div>
      </form>
    </SidePanel>
  )
}

function InvestmentPanel({
  investment: i,
  onClose,
  onChanged,
}: {
  investment: Investment
  onClose: () => void
  onChanged: () => void
}) {
  const { can } = useAuth()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<DetailsForm>(formOf(i))
  const [closing, setClosing] = useState(false)
  const [reason, setReason] = useState('')
  //: null: no entry form; 'new': returns or a loss; 'opening': the opening
  //: balance; an entry: correcting it.
  const [entryForm, setEntryForm] = useState<'new' | 'opening' | Entry | null>(null)
  const [removing, setRemoving] = useState<Entry | null>(null)

  const history = useQuery({
    queryKey: ['investment-history', i.id],
    queryFn: () => api.get<History>(`/investments/${i.id}/history`),
  })
  const act = useMutation({
    mutationFn: (v: { path: string; body: unknown; method?: 'post' | 'patch' }) =>
      v.method === 'patch' ? api.patch(v.path, v.body) : api.post(v.path, v.body),
    onSuccess: () => {
      setEditing(false)
      setClosing(false)
      setRemoving(null)
      setReason('')
      onChanged()
    },
  })
  const active = i.status === 'approved'
  const record = can('gl.journal.create')
  const edit = can('party.vendor.edit')
  const problem = problemsOf(form)
  const entries = history.data?.entries ?? []
  const byId = new Map(entries.map((e) => [e.id, e]))
  const openingEntry = entries.find((e) => e.kind === 'opening' && e.status === 'posted')
  const dates = {
    earliest: history.data?.opening_earliest ?? null,
    latest: history.data?.opening_latest ?? todayHere(),
  }
  const idle = !entryForm && !removing

  return (
    <SidePanel
      kicker={`Investment ledger · ${i.code}`}
      title={i.name}
      sub={[i.type_label, i.institution, i.reference].filter(Boolean).join(' · ')}
      amount={formatInr(i.balance_paise)}
      badge={
        <span className={`pill ${active ? 'approved' : 'draft'}`}>
          {active ? 'active' : 'closed'}
        </span>
      }
      wide
      onClose={onClose}
      foot={
        <>
          {active && record && !entryForm && (
            <button className="primary" onClick={() => setEntryForm('new')}>
              Add entry
            </button>
          )}
          {edit && !editing && (
            <button
              className="quiet"
              onClick={() => {
                setForm(formOf(i))
                setEditing(true)
              }}
            >
              Correct details
            </button>
          )}
          {edit && !closing && (
            <button className="quiet" onClick={() => setClosing(true)}>
              {active ? 'Close ledger' : 'Reopen ledger'}
            </button>
          )}
        </>
      }
    >
      <ErrorNote error={act.error ?? history.error} />
      <dl className="appr-facts">
        <div>
          <dt>Total invested</dt>
          <dd className="strong">{formatInr(i.total_invested_paise)}</dd>
        </div>
        <div>
          <dt>Opening · put in · taken out</dt>
          <dd>
            {formatInr(i.opening_paise)} · {formatInr(i.invested_paise)} ·{' '}
            {formatInr(i.redeemed_paise)}
          </dd>
        </div>
        <div>
          <dt>Returns earned</dt>
          <dd>
            {formatInr(i.returns_paise)}
            {i.tds_paise ? ` (TDS ${formatInr(i.tds_paise)})` : ''}
          </dd>
        </div>
        <div>
          <dt>Started · matures</dt>
          <dd>
            {i.started_on ?? '—'} · {i.maturity_on ?? '—'}
            {i.maturity_on && <span className="cell-sub"> {maturityNote(i)}</span>}
          </dd>
        </div>
        <div>
          <dt>Rate · maturity amount</dt>
          <dd>
            {i.rate_bp != null ? `${i.rate_bp / 100}%` : '—'} ·{' '}
            {i.maturity_amount_paise ? formatInr(i.maturity_amount_paise) : '—'}
          </dd>
        </div>
        <div>
          <dt>Raised by</dt>
          <dd>{i.raised_by ?? '—'}</dd>
        </div>
      </dl>
      {i.notes && <p className="muted">{i.notes}</p>}
      {!active && (
        <Note kind="info">
          Closed: bank lines and new entries can no longer name this investment. Its history stays.
        </Note>
      )}

      {editing && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!problem)
              act.mutate({ path: `/investments/${i.id}`, method: 'patch', body: bodyOf(form) })
          }}
        >
          <DetailsFields form={form} setForm={setForm} />
          {problem && <Note kind="warn">{problem}</Note>}
          <div className="actions">
            <button className="primary" type="submit" disabled={act.isPending || !!problem}>
              Save
            </button>
            <button type="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {closing && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            act.mutate({ path: `/investments/${i.id}/open`, body: { is_open: !active, reason } })
          }}
        >
          <Field
            label={active ? 'Why is this ledger being closed?' : 'Why is it being reopened?'}
            caption={
              active
                ? 'e.g. matured and paid out; new bank lines can no longer name it, the history stays'
                : 'e.g. topped up again'
            }
          >
            <input value={reason} onChange={(e) => setReason(e.target.value)} required />
          </Field>
          <div className="actions">
            <button
              className="primary"
              type="submit"
              disabled={act.isPending || reason.trim().length < 3}
            >
              {active ? 'Close ledger' : 'Reopen ledger'}
            </button>
            <button type="button" onClick={() => setClosing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {history.data && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ margin: '0 0 8px' }}>Opening balance</h3>
          {openingEntry ? (
            <dl className="appr-facts">
              <div>
                <dt>Stood at</dt>
                <dd className="strong">{formatInr(openingEntry.amount_paise)}</dd>
              </div>
              <div>
                <dt>As at</dt>
                <dd className="date">{openingEntry.entry_date}</dd>
              </div>
              <div>
                <dt>Where the figure comes from</dt>
                <dd>{openingEntry.description}</dd>
              </div>
              <div>
                <dt>Posted as</dt>
                <dd className="mono">{openingEntry.journal_number ?? '—'}</dd>
              </div>
              <div>
                <dt>Recorded by</dt>
                <dd>{openingEntry.recorded_by ?? '—'}</dd>
              </div>
            </dl>
          ) : (
            <Note kind="info">
              No opening balance. If {i.name} was already held when these books began
              {dates.earliest ? ` (${dates.earliest})` : ''}, record what it stood at.
            </Note>
          )}
          {record && active && idle && (
            <div className="actions">
              {openingEntry ? (
                <>
                  <button onClick={() => setEntryForm(openingEntry)}>Correct</button>
                  <button
                    className="quiet"
                    onClick={() => {
                      setReason('')
                      setRemoving(openingEntry)
                    }}
                  >
                    Remove
                  </button>
                </>
              ) : (
                <button onClick={() => setEntryForm('opening')}>Add opening balance</button>
              )}
            </div>
          )}
        </div>
      )}

      {entryForm && (
        <EntryForm
          investment={i}
          entry={typeof entryForm === 'string' ? null : entryForm}
          opening={entryForm === 'opening'}
          dates={dates}
          onClose={() => setEntryForm(null)}
          onDone={() => {
            setEntryForm(null)
            onChanged()
          }}
        />
      )}

      {removing && (
        <form
          className="card"
          style={{ marginTop: 16 }}
          onSubmit={(e) => {
            e.preventDefault()
            act.mutate({
              path: `/investments/${i.id}/entries/${removing.id}/reverse`,
              body: { reason },
            })
          }}
        >
          <h3 style={{ margin: '0 0 8px' }}>
            Remove {removing.kind_label.toLowerCase()} of {formatInr(removing.amount_paise)}
          </h3>
          <Note kind="info">
            A reversing journal takes it back out of the books. The entry and its reversal both stay
            in the history.
          </Note>
          <Field label="Why is it being removed?" caption="e.g. recorded twice">
            <input value={reason} onChange={(e) => setReason(e.target.value)} required />
          </Field>
          <div className="actions">
            <button
              className="primary"
              type="submit"
              disabled={act.isPending || reason.trim().length < 3}
            >
              Remove entry
            </button>
            <button type="button" onClick={() => setRemoving(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <h3 style={{ margin: '16px 0 8px' }}>Ledger</h3>
      {history.isLoading ? (
        <Loading what="Loading the ledger" />
      ) : (history.data?.lines ?? []).length === 0 ? (
        <div className="empty">
          Nothing on this ledger yet. Money put in or taken out comes from its bank line (category
          Investment); the opening balance and returns earned are added here.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="appr-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Entry</th>
                <th className="num">In (out)</th>
                <th className="num">Returns</th>
                <th className="num">Balance</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(history.data?.lines ?? []).map((l) => {
                const entry = l.entry_id ? byId.get(l.entry_id) : undefined
                const live = entry && entry.status === 'posted' && !l.reversal
                return (
                  <tr key={`${l.journal_entry_id}`}>
                    <td className="date">{l.date}</td>
                    <td>
                      <div className="ref">
                        {l.journal_number}{' '}
                        <span className={`pill ${l.reversal ? 'returned' : 'approved'}`}>
                          {l.reversal ? 'reversal' : (LINE_LABEL[l.kind] ?? l.kind)}
                        </span>
                        {l.bank_transaction_id && <span className="pill draft">bank line</span>}
                      </div>
                      <div
                        className="cell-sub clipped"
                        title={entry?.description ?? l.bank_reference ?? l.narration}
                      >
                        {entry?.description ?? l.bank_reference ?? l.narration}
                      </div>
                      {entry && !l.reversal && entry.status === 'reversed' && (
                        <div className="cell-sub muted">
                          {entry.replaced_by_id ? 'corrected' : 'removed'}
                          {entry.reason ? `: ${entry.reason}` : ''}
                        </div>
                      )}
                    </td>
                    <td className="num">{l.holding_paise ? formatInr(l.holding_paise) : ''}</td>
                    <td className="num">
                      {l.returns_paise ? formatInr(l.returns_paise) : ''}
                      {l.tds_paise ? (
                        <div className="cell-sub">TDS {formatInr(l.tds_paise)}</div>
                      ) : null}
                    </td>
                    <td className="num strong">{formatInr(l.balance_paise)}</td>
                    <td>
                      {live && record && active && idle && (
                        <div className="actions" style={{ margin: 0 }}>
                          <button className="quiet" onClick={() => setEntryForm(entry)}>
                            Edit
                          </button>
                          <button
                            className="quiet"
                            onClick={() => {
                              setReason('')
                              setRemoving(entry)
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </SidePanel>
  )
}

function EntryForm({
  investment: i,
  entry,
  opening,
  dates,
  onClose,
  onDone,
}: {
  investment: Investment
  entry: Entry | null
  //: A new opening balance (from its card), not returns or a loss.
  opening: boolean
  //: The dates an opening balance may carry.
  dates: { earliest: string | null; latest: string }
  onClose: () => void
  onDone: () => void
}) {
  const [form, setForm] = useState({
    kind: (entry?.kind ?? (opening ? 'opening' : 'return')) as EntryKind,
    entry_date: entry?.entry_date ?? '',
    description: entry?.description ?? '',
    amount: rupees(entry?.amount_paise ?? null),
    tds: rupees(entry?.tds_paise ?? null),
    notes: entry?.notes ?? '',
    reason: '',
  })
  const amount = toPaise(form.amount)
  const tds = form.kind === 'return' && form.tds ? toPaise(form.tds) : 0
  const today = todayHere()
  const isOpening = form.kind === 'opening'
  //: Correcting the opening balance itself: its own date is still allowed.
  const latest = isOpening && entry ? today : dates.latest
  const problem = !form.entry_date
    ? 'Give the date.'
    : isOpening && openingDateProblem(form.entry_date, dates.earliest, latest)
      ? openingDateProblem(form.entry_date, dates.earliest, latest)
      : form.entry_date > today
        ? 'The date cannot be in the future.'
        : form.description.trim().length < 3
          ? isOpening
            ? 'Say where the figure comes from.'
            : 'Describe the entry (three letters or more).'
          : !(amount > 0)
            ? 'The amount must be more than zero.'
            : tds < 0 || (tds > 0 && tds >= amount)
              ? 'TDS must be less than the amount.'
              : entry && form.reason.trim().length < 3
                ? 'Say why it is being corrected.'
                : null
  const save = useMutation({
    mutationFn: () => {
      const body = {
        entry_date: form.entry_date,
        description: form.description.trim(),
        amount_paise: amount,
        tds_paise: tds,
        notes: form.notes.trim() || null,
      }
      return entry
        ? api.put(`/investments/${i.id}/entries/${entry.id}`, {
            ...body,
            reason: form.reason.trim(),
          })
        : api.post(`/investments/${i.id}/entries`, { ...body, kind: form.kind })
    },
    onSuccess: onDone,
  })
  const kind = ENTRY_KINDS.find((k) => k.value === form.kind)

  return (
    <form
      className="card"
      style={{ marginTop: 16 }}
      onSubmit={(e) => {
        e.preventDefault()
        if (!problem) save.mutate()
      }}
    >
      <h3 style={{ margin: '0 0 8px' }}>
        {entry
          ? `Correct ${entry.kind_label.toLowerCase()}`
          : opening
            ? 'Add opening balance'
            : 'Add an entry'}
      </h3>
      <ErrorNote error={save.error} />
      <div className="row">
        <Field label="What" caption={kind?.caption}>
          <select
            value={form.kind}
            disabled={!!entry || opening}
            onChange={(e) => setForm({ ...form, kind: e.target.value as EntryKind })}
          >
            {/* The opening balance has its own card; here only what it is. */}
            {ENTRY_KINDS.filter((k) => (k.value === 'opening') === isOpening).map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={isOpening ? 'As at' : 'Date'}
          caption={
            isOpening
              ? `${dates.earliest ? `from ${dates.earliest}` : ''}${
                  dates.earliest ? ' ' : ''
                }to ${latest}`
              : undefined
          }
        >
          <input
            type="date"
            min={isOpening ? (dates.earliest ?? undefined) : undefined}
            max={isOpening ? latest : today}
            value={form.entry_date}
            onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
            required
          />
        </Field>
      </div>
      <Field
        label={isOpening ? 'Where the figure comes from' : 'Description'}
        caption={
          form.kind === 'opening'
            ? "e.g. balance per CA's confirmation, 31 Mar 2026"
            : form.kind === 'return'
              ? 'e.g. Q1 interest credited to the FD'
              : 'e.g. redeemed below cost'
        }
      >
        <input
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          maxLength={300}
          required
        />
      </Field>
      <div className="row">
        <Field
          label={form.kind === 'return' ? 'Amount earned, before TDS (₹)' : 'Amount (₹)'}
          caption={
            form.kind === 'return' ? 'gross: the interest or gain before any TDS' : undefined
          }
        >
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            required
          />
        </Field>
        {form.kind === 'return' && (
          <Field label="TDS deducted (₹)" caption="optional; recoverable, booked to 1430">
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.tds}
              onChange={(e) => setForm({ ...form, tds: e.target.value })}
            />
          </Field>
        )}
      </div>
      {form.kind === 'return' && amount > 0 && (
        <p className="muted" style={{ fontSize: 14 }}>
          Adds {formatInr(amount - tds)} to the investment
          {tds ? ` and ${formatInr(tds)} to TDS receivable` : ''}; {formatInr(amount)} to income
          from investments.
        </p>
      )}
      <Field label="Notes" caption="optional">
        <textarea
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          maxLength={2000}
        />
      </Field>
      {entry && (
        <Field
          label="Why is it being corrected?"
          caption="the old entry is reversed and the corrected one posted; both stay in the history"
        >
          <input
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            required
          />
        </Field>
      )}
      {problem && form.entry_date && <Note kind="warn">{problem}</Note>}
      <div className="actions">
        <button className="primary" type="submit" disabled={save.isPending || !!problem}>
          {save.isPending ? 'Posting…' : entry ? 'Correct and post' : 'Post entry'}
        </button>
        <button type="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  )
}
